import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { api, ApiError } from "../lib/api";
import { useToast } from "../components/Toast";
import { SignedAmount } from "../components/SignedAmount";
import { formatDate } from "../lib/format";

/**
 * Einstellungen › Konten (Block 3b, 18.10.2026). Je Konto das Format des Kontoauszugs (Spaltennamen, Trennzeichen, Kodierung …) —
 * eine Einstellung, kein Code je Bank. Import: Datei wählen → Vorschau (nichts geschrieben) → „Übernehmen“. Neue Buchungen sind
 * unbestätigt; Zeilen, die es auf dem Konto schon gibt (gleiches Datum und gleicher Betrag, auch aus anderen Quellen), werden nie doppelt angelegt.
 */
type Format = {
  trennzeichen: string; kodierung: "utf-8" | "windows-1250"; zahlenformat: "de" | "en"; datumsformat: string; datum: string; betrag: string;
  text: string[]; gegenpartei?: string[]; filter?: { spalte: string; werte: string[] };
  zusatzzeile?: { betrag: string; textPraefix: string; text: string[]; gegenpartei: string }; pdf?: "hanseatic" | "advanzia";
};
type Konto = { id: string; name: string; kontoart: string; kuerzel: string | null; importformat: Format | null; aktiv: boolean; buchungen: number; unbestaetigt: number };
type FremdTreffer = { datum: string; betragCent: number; textDatei: string; textVorhanden: string | null; quelle: string };
type Bericht = { zeilen: number; neu: number; vorhanden: number; summeNeuCent: number; summeDateiCent: number; vertraege: number; hinweise: string[];
  fremdTreffer: FremdTreffer[]; ausgefuehrt: boolean; von: string | null; bis: string | null };

const DATUMSFORMATE = ["TT.MM.JJ", "TT.MM.JJJJ", "TT-MM-JJJJ", "TT/MM/JJ", "JJJJ-MM-TT"];
const LEER: Format = { trennzeichen: ";", kodierung: "utf-8", zahlenformat: "de", datumsformat: "TT.MM.JJJJ", datum: "", betrag: "", text: [] };
const feld = "border border-ios-line rounded-lg px-3 py-2 text-[16px] lg:text-[14px] w-full bg-white";
const fehlertext = (e: unknown) => (e instanceof ApiError ? e.message : "Aktion fehlgeschlagen");
const liste = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

async function sendeAuszug(kontoId: string, dateien: File[], ausfuehren: boolean): Promise<Bericht> {
  const fd = new FormData();
  for (const d of dateien) fd.append("datei", d);
  const r = await fetch(`/api/konten/${kontoId}/auszug${ausfuehren ? "?ausfuehren=1" : ""}`, { method: "POST", body: fd, credentials: "same-origin" });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, (body as { error?: string }).error ?? `HTTP ${r.status}`, body);
  return body as Bericht;
}

export function KontenPage() {
  const { data, isLoading } = useQuery<Konto[]>({ queryKey: ["konten"], queryFn: () => api.get("/api/konten") });
  return (
    <div className="max-w-2xl mx-auto p-4 lg:p-6">
      <Link to="/einstellungen" className="inline-flex items-center gap-1 text-ios-blue text-[14px] mb-3">
        <ChevronLeftIcon className="w-4 h-4" /> Einstellungen
      </Link>
      <h1 className="text-[22px] font-semibold tracking-tight mb-1">Konten</h1>
      <p className="text-[13px] text-ios-muted mb-4">
        Kontoauszüge importierst du je Konto als CSV-Datei. Erst siehst du eine Vorschau, dann übernimmst du. Neue Buchungen sind unbestätigt
        und erscheinen unter „Buchungen“. Was es auf dem Konto schon gibt, wird nicht doppelt angelegt.
      </p>
      {isLoading ? <p className="text-[14px] text-ios-muted">Lädt …</p> : (
        <div className="space-y-3">
          {(data ?? []).map((k) => <KontoKarte key={k.id} k={k} />)}
          {(data ?? []).length === 0 && <p className="text-[14px] text-ios-muted">Noch keine Konten.</p>}
          <NeuesKonto />
        </div>
      )}
    </div>
  );
}

const KONTOARTEN: [string, string][] = [["bank", "Bankkonto"], ["kreditkarte", "Kreditkarte"], ["bar", "Bargeld"], ["kredit", "Kredit"], ["sonstiges", "Sonstiges"]];

/** Neues Konto: Name, Kontoart und Kürzel (für die Beleg-Nummern „Kürzel-Jahr-Nr“ neuer Buchungen). */
function NeuesKonto() {
  const qc = useQueryClient();
  const toast = useToast();
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState(""), [art, setArt] = useState("bank"), [kuerzel, setKuerzel] = useState("");
  const anlegen = useMutation({
    mutationFn: () => api.post("/api/konten", { name, kontoart: art, kuerzel }),
    onSuccess: () => { toast.success("Konto angelegt"); setOffen(false); setName(""); setKuerzel(""); void qc.invalidateQueries({ queryKey: ["konten"] }); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  if (!offen) return <button type="button" className="text-[14px] text-ios-blue" onClick={() => setOffen(true)}>+ Konto anlegen</button>;
  return (
    <div className="bg-white rounded-iosLg shadow-card p-4 space-y-2">
      <div className="text-[15px] font-medium">Neues Konto</div>
      <input className={feld} placeholder="Name (z. B. Kreditkarte Bank X)" value={name} onChange={(e) => setName(e.target.value)} />
      <select className={feld} value={art} onChange={(e) => setArt(e.target.value)} aria-label="Kontoart">
        {KONTOARTEN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <input className={feld} placeholder="Kürzel für Beleg-Nummern (2–6 Zeichen, z. B. KKX)" value={kuerzel} onChange={(e) => setKuerzel(e.target.value.toUpperCase())} />
      <div className="flex gap-2">
        <button type="button" disabled={!name.trim() || !kuerzel.trim() || anlegen.isPending} onClick={() => anlegen.mutate()}
          className="px-3 py-1.5 rounded-ios bg-ios-blue text-white text-[14px] disabled:opacity-40">anlegen</button>
        <button type="button" className="px-3 py-1.5 text-[14px] text-ios-blue" onClick={() => setOffen(false)}>abbrechen</button>
      </div>
    </div>
  );
}

/** Kürzel des Kontos ändern. Vorhandene Beleg-Nummern bleiben; Buchungen ohne Nummer bekommen sofort eine. */
function Kuerzel({ k, onFertig }: { k: Konto; onFertig: () => void }) {
  const toast = useToast();
  const [wert, setWert] = useState(k.kuerzel ?? ""), [edit, setEdit] = useState(false);
  const speichern = useMutation({
    mutationFn: () => api.put<{ neueNummern: number }>(`/api/konten/${k.id}/kuerzel`, { kuerzel: wert }),
    onSuccess: (r) => { toast.success(r.neueNummern ? `gespeichert · ${r.neueNummern} Buchungen haben jetzt eine Beleg-Nummer` : "gespeichert"); setEdit(false); onFertig(); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  if (!edit) return (
    <div className="text-[13px]">
      {k.kuerzel ? <>Kürzel für Beleg-Nummern: <b>{k.kuerzel}</b></> : <span className="text-ios-blue">Kein Kürzel — neue Buchungen bekommen keine Beleg-Nummer.</span>}{" "}
      <button type="button" className="text-ios-blue" onClick={() => setEdit(true)}>{k.kuerzel ? "ändern" : "festlegen"}</button>
    </div>
  );
  return (
    <div className="flex flex-wrap gap-2 items-center text-[13px]">
      <input className="border border-ios-line rounded-lg px-2 py-1 text-[16px] lg:text-[14px] w-28" value={wert} aria-label="Kürzel"
        onChange={(e) => setWert(e.target.value.toUpperCase())} />
      <button type="button" disabled={speichern.isPending} onClick={() => speichern.mutate()} className="px-3 py-1 rounded-ios bg-ios-blue text-white disabled:opacity-40">speichern</button>
      <button type="button" className="text-ios-blue" onClick={() => { setWert(k.kuerzel ?? ""); setEdit(false); }}>abbrechen</button>
    </div>
  );
}

function KontoKarte({ k }: { k: Konto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [formatOffen, setFormatOffen] = useState(false);
  const [dateien, setDateien] = useState<File[]>([]);
  const [vorschau, setVorschau] = useState<Bericht | null>(null);
  const neuLaden = () => { void qc.invalidateQueries({ queryKey: ["konten"] }); void qc.invalidateQueries({ queryKey: ["buchungen"] }); void qc.invalidateQueries({ queryKey: ["buchungen-jahre"] }); };
  const pruefen = useMutation({
    mutationFn: (f: File[]) => sendeAuszug(k.id, f, false), onSuccess: setVorschau, onError: (e) => { setVorschau(null); toast.error(fehlertext(e)); },
  });
  const uebernehmen = useMutation({
    mutationFn: () => sendeAuszug(k.id, dateien, true),
    onSuccess: (r) => { toast.success(`${r.neu} neue Buchungen übernommen (unbestätigt)`); setVorschau(null); setDateien([]); neuLaden(); },
    onError: (e) => toast.error(fehlertext(e)),
  });

  return (
    <div className="bg-white rounded-iosLg shadow-card p-4 space-y-3">
      <div className="flex items-baseline gap-2">
        <span className="text-[16px] font-medium flex-1">{k.name}</span>
        <span className="text-[13px] text-ios-muted">{k.buchungen} Buchungen{k.unbestaetigt ? ` · ${k.unbestaetigt} unbestätigt` : ""}</span>
      </div>
      <Kuerzel k={k} onFertig={neuLaden} />
      {k.importformat ? (
        <div className="space-y-2">
          <input type="file" accept={k.importformat.pdf ? ".csv,.txt,text/csv,.pdf,application/pdf" : ".csv,.txt,text/csv"} multiple={!!k.importformat.pdf}
            className="text-[14px]"
            onChange={(e) => { const f = [...(e.target.files ?? [])]; setDateien(f); setVorschau(null); if (f.length) pruefen.mutate(f); }} />
          {k.importformat.pdf && <p className="text-[13px] text-ios-muted">PDF-Auszüge der Bank: mehrere auf einmal möglich. Jeder Auszug wird über den Saldo geprüft (alter Saldo + Bewegungen = neuer Saldo) — stimmt einer nicht, wird nichts übernommen.</p>}
          {pruefen.isPending && <p className="text-[13px] text-ios-muted">Prüfe Datei …</p>}
          {vorschau && <Vorschau r={vorschau} />}
          {vorschau && (
            <button type="button" disabled={vorschau.neu === 0 || uebernehmen.isPending} onClick={() => uebernehmen.mutate()}
              className="px-3 py-1.5 rounded-ios bg-ios-blue text-white text-[14px] disabled:opacity-40">
              {vorschau.neu === 0 ? "nichts Neues" : `${vorschau.neu} neue Buchungen übernehmen`}
            </button>
          )}
        </div>
      ) : <p className="text-[13px] text-ios-blue">Für dieses Konto ist noch kein Kontoauszug-Format eingestellt.</p>}
      <button type="button" className="text-[13px] text-ios-blue" onClick={() => setFormatOffen(!formatOffen)}>
        {formatOffen ? "Format schließen" : "Kontoauszug-Format bearbeiten"}
      </button>
      {formatOffen && <FormatEditor k={k} onFertig={() => { setFormatOffen(false); neuLaden(); }} />}
    </div>
  );
}

function Vorschau({ r }: { r: Bericht }) {
  return (
    <div className="text-[13px] bg-ios-bg/60 rounded-lg p-3 space-y-1">
      <div>{r.zeilen} Zeilen{r.von ? ` vom ${formatDate(r.von)} bis ${formatDate(r.bis)}` : ""} · Σ Datei <SignedAmount value={r.summeDateiCent / 100} treatNegativeAsIncome={false} /></div>
      <div><b>{r.neu} neu</b> (Σ <SignedAmount value={r.summeNeuCent / 100} treatNegativeAsIncome={false} />) · {r.vorhanden} schon vorhanden</div>
      {r.fremdTreffer.length > 0 && (
        <details>
          <summary className="cursor-pointer text-ios-blue">{r.fremdTreffer.length} Zeilen gegen Buchungen aus anderen Quellen als vorhanden gezählt — bitte ansehen</summary>
          {r.fremdTreffer.map((f, i) => (
            <div key={i} className="mt-1">{formatDate(f.datum)} · <SignedAmount value={f.betragCent / 100} treatNegativeAsIncome={false} /> · Datei: {f.textDatei || "—"}
              <span className="text-ios-muted"> ↔ vorhanden ({f.quelle}): {f.textVorhanden ?? "—"}</span></div>
          ))}
        </details>
      )}
      {r.hinweise.map((h, i) => <div key={i} className="text-ios-blue">{h}</div>)}
    </div>
  );
}

function FormatEditor({ k, onFertig }: { k: Konto; onFertig: () => void }) {
  const toast = useToast();
  const [f, setF] = useState<Format>(k.importformat ?? LEER);
  const [mitFilter, setMitFilter] = useState(!!k.importformat?.filter);
  const [mitZusatz, setMitZusatz] = useState(!!k.importformat?.zusatzzeile);
  const speichern = useMutation({
    mutationFn: () => {
      const { filter, zusatzzeile, ...rest } = f;
      return api.put(`/api/konten/${k.id}/importformat`, { importformat: { ...rest, ...(mitFilter ? { filter } : {}), ...(mitZusatz ? { zusatzzeile } : {}) } });
    },
    onSuccess: () => { toast.success("Format gespeichert"); onFertig(); }, onError: (e) => toast.error(fehlertext(e)),
  });
  const t = (label: string, el: React.ReactNode) => <label className="text-[13px] text-ios-muted block">{label}{el}</label>;
  return (
    <form className="space-y-3 border-t border-ios-line pt-3" onSubmit={(e) => { e.preventDefault(); speichern.mutate(); }}>
      <div className="grid grid-cols-2 gap-3">
        {t("Trennzeichen", <input className={feld} maxLength={1} value={f.trennzeichen} onChange={(e) => setF({ ...f, trennzeichen: e.target.value })} />)}
        {t("Kodierung", <select className={feld} value={f.kodierung} onChange={(e) => setF({ ...f, kodierung: e.target.value as Format["kodierung"] })}>
          <option value="utf-8">UTF-8</option><option value="windows-1250">Windows (alte Banken)</option></select>)}
        {t("Zahlenformat", <select className={feld} value={f.zahlenformat} onChange={(e) => setF({ ...f, zahlenformat: e.target.value as Format["zahlenformat"] })}>
          <option value="de">1.234,56</option><option value="en">1,234.56</option></select>)}
        {t("Datumsformat", <select className={feld} value={f.datumsformat} onChange={(e) => setF({ ...f, datumsformat: e.target.value })}>
          {DATUMSFORMATE.map((d) => <option key={d} value={d}>{d}</option>)}</select>)}
        {t("Spalte Datum", <input className={feld} value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} />)}
        {t("Spalte Betrag", <input className={feld} value={f.betrag} onChange={(e) => setF({ ...f, betrag: e.target.value })} />)}
      </div>
      {t("Text-Spalten (mit Komma getrennt)", <input className={feld} value={f.text.join(", ")} onChange={(e) => setF({ ...f, text: liste(e.target.value) })} />)}
      {t("Gegenpartei-Spalten (erste nicht leere zählt)", <input className={feld} value={(f.gegenpartei ?? []).join(", ")} onChange={(e) => setF({ ...f, gegenpartei: liste(e.target.value) })} />)}
      <label className="flex items-center gap-2 text-[14px]"><input type="checkbox" checked={mitFilter} onChange={(e) => setMitFilter(e.target.checked)} /> nur Zeilen mit bestimmten Werten in einer Spalte</label>
      {mitFilter && <div className="grid grid-cols-2 gap-3">
        {t("Spalte", <input className={feld} value={f.filter?.spalte ?? ""} onChange={(e) => setF({ ...f, filter: { spalte: e.target.value, werte: f.filter?.werte ?? [] } })} />)}
        {t("Werte (Komma)", <input className={feld} value={(f.filter?.werte ?? []).join(", ")} onChange={(e) => setF({ ...f, filter: { spalte: f.filter?.spalte ?? "", werte: liste(e.target.value) } })} />)}
      </div>}
      {t("PDF-Auszug der Bank (Kreditkarte)", <select className={feld} value={f.pdf ?? ""} onChange={(e) => {
        const { pdf: _alt, ...ohne } = f; setF(e.target.value ? { ...ohne, pdf: e.target.value as NonNullable<Format["pdf"]> } : ohne); }}>
        <option value="">keiner (nur CSV)</option><option value="hanseatic">Hanseatic GoldCard</option><option value="advanzia">Advanzia</option>
      </select>)}
      <label className="flex items-center gap-2 text-[14px]"><input type="checkbox" checked={mitZusatz} onChange={(e) => setMitZusatz(e.target.checked)} /> Zusatzzeile als eigene Buchung (z. B. PayPal-Gebühr)</label>
      {mitZusatz && <div className="grid grid-cols-2 gap-3">
        {t("Spalte Betrag", <input className={feld} value={f.zusatzzeile?.betrag ?? ""} onChange={(e) => setF({ ...f, zusatzzeile: { ...(f.zusatzzeile ?? { textPraefix: "", text: [], gegenpartei: "" }), betrag: e.target.value } })} />)}
        {t("Text-Vorsatz", <input className={feld} value={f.zusatzzeile?.textPraefix ?? ""} onChange={(e) => setF({ ...f, zusatzzeile: { ...(f.zusatzzeile ?? { betrag: "", text: [], gegenpartei: "" }), textPraefix: e.target.value } })} />)}
        {t("Text-Spalten (Komma)", <input className={feld} value={(f.zusatzzeile?.text ?? []).join(", ")} onChange={(e) => setF({ ...f, zusatzzeile: { ...(f.zusatzzeile ?? { betrag: "", textPraefix: "", gegenpartei: "" }), text: liste(e.target.value) } })} />)}
        {t("Gegenpartei", <input className={feld} value={f.zusatzzeile?.gegenpartei ?? ""} onChange={(e) => setF({ ...f, zusatzzeile: { ...(f.zusatzzeile ?? { betrag: "", textPraefix: "", text: [] }), gegenpartei: e.target.value } })} />)}
      </div>}
      <div className="flex justify-end">
        <button type="submit" disabled={speichern.isPending} className="text-[14px] px-3 py-1.5 rounded-ios bg-ios-blue text-white disabled:opacity-50">Format speichern</button>
      </div>
    </form>
  );
}
